import React from 'react';
import { Check, X, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ComparisonTable = () => {
  const { t } = useTranslation();
  const features = [
    { id: 'meta',            yoast: true,      orchestra: true },
    { id: 'sitemap',         yoast: true,      orchestra: true },
    { id: 'og',              yoast: true,      orchestra: true },
    { id: 'schema',          yoast: true,      orchestra: true },
    { id: 'canonical',       yoast: true,      orchestra: true },
    { id: 'redirect',        yoast: 'premium', orchestra: true },
    { id: 'cornerstone',     yoast: 'premium', orchestra: true },
    { id: 'aiNative',        yoast: false,     orchestra: true },
    { id: 'fullArticle',     yoast: false,     orchestra: true },
    { id: 'brandVoice',      yoast: false,     orchestra: true, badgeKey: 'unique' },
    { id: 'autoPilot',       yoast: false,     orchestra: true, badgeKey: 'unique' },
    { id: 'keywordResearch', yoast: false,     orchestra: true },
    { id: 'aeoScore',        yoast: false,     orchestra: true },
    { id: 'llmsTxt',         yoast: false,     orchestra: true, badgeKey: 'firstInWorld' },
    { id: 'analytics',       yoast: false,     orchestra: true, badgeKey: 'unique' },
    { id: 'contentCalendar', yoast: false,     orchestra: true },
    { id: 'imageSeo',        yoast: false,     orchestra: true, badgeKey: 'unique' },
    { id: 'refund',          yoast: false,     orchestra: true, badgeKey: 'unique' },
    { id: 'aiTracking',      yoast: false,     orchestra: true, badgeKey: 'firstInWorld' },
    { id: 'canniFix',        yoast: false,     orchestra: true },
    { id: 'ai404',           yoast: false,     orchestra: true },
    { id: 'gsc',             yoast: false,     orchestra: true },
    { id: 'localSeo',        yoast: false,     orchestra: true },
    { id: 'migration',       yoast: false,     orchestra: true },
    { id: 'multilang',       yoast: false,     orchestra: true },
    { id: 'compat',          yoast: 'dash',    orchestra: true },
  ];

  return (
    <section className="py-24 px-6 bg-[#0a0a0a]" data-testid="comparison-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12 animate-fade-up">
          <span className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-semibold">{t('comparison.eyebrow')}</span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mt-4 mb-4">
            {t('comparison.title')}
          </h2>
          <p className="text-base text-[#A1A1AA] max-w-2xl mx-auto mt-4">
            {t('comparison.subtitle')}
          </p>
        </div>

        <div className="overflow-x-auto w-full rounded-2xl border border-white/10 overflow-hidden animate-fade-up" style={{ animationDelay: '0.1s' }}>
          {/* Header */}
          <div className="grid grid-cols-[1fr_70px_90px] sm:grid-cols-[1fr_110px_140px] md:grid-cols-[1fr_140px_180px] bg-[#111111] border-b border-white/10">
            <div className="px-3 py-3 sm:px-6 sm:py-4 text-xs sm:text-sm font-medium text-[#A1A1AA]">{t('comparison.headerFeature')}</div>
            <div className="px-2 py-3 sm:px-4 sm:py-4 text-xs sm:text-sm font-medium text-[#A1A1AA] text-center border-l border-white/5">
              <span className="hidden sm:inline">{t('comparison.headerCompetitor')}</span>
              <span className="sm:hidden">Yoast/<br/>RankMath</span>
            </div>
            <div className="px-2 py-3 sm:px-4 sm:py-4 text-xs sm:text-sm font-medium text-center border-l border-white/5 bg-[#0055FF]/10 text-[#0055FF]">
              <span className="hidden sm:inline">{t('comparison.headerOrchestra')}</span>
              <span className="sm:hidden">SEO<br/>Orchestra</span>
            </div>
          </div>

          {/* Rows */}
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className={`grid grid-cols-[1fr_70px_90px] sm:grid-cols-[1fr_110px_140px] md:grid-cols-[1fr_140px_180px] ${index < features.length - 1 ? 'border-b border-white/5' : ''} hover:bg-white/[0.02] transition-colors`}
            >
              <div className="px-3 py-3 sm:px-6 sm:py-4 text-xs sm:text-sm text-white/90 leading-tight">
                {t(`comparison.rows.${feature.id}`)}
                {feature.badgeKey && (
                  <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-gradient-to-r from-[#10B981] to-[#00E5FF] text-black align-middle">
                    {t(`comparison.badges.${feature.badgeKey}`)}
                  </span>
                )}
              </div>
              <div className="px-2 py-3 sm:px-4 sm:py-4 flex items-center justify-center border-l border-white/5">
                {feature.yoast === true && <Check className="w-4 h-4 sm:w-5 sm:h-5 text-[#A1A1AA]" />}
                {feature.yoast === false && <X className="w-4 h-4 sm:w-5 sm:h-5 text-[#EF4444]/50" />}
                {feature.yoast === 'dash' && <Minus className="w-4 h-4 sm:w-5 sm:h-5 text-[#A1A1AA]/40" />}
                {feature.yoast === 'premium' && (
                  <span className="text-[10px] sm:text-xs text-[#F59E0B] font-semibold whitespace-nowrap">{t('comparison.premiumLabel')}</span>
                )}
              </div>
              <div className="px-2 py-3 sm:px-4 sm:py-4 flex items-center justify-center border-l border-white/5 bg-[#0055FF]/[0.03]">
                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-[#10B981]" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ComparisonTable;
